import { Link } from "react-router-dom";
import { ArrowUpRight, Swords, LayoutGrid, Box, Layers, ShieldCheck, Trophy, Users, Sparkles, Handshake } from "lucide-react";
import { IMAGES, DISCIPLINES, ARENAS, NEWS_ITEMS } from "../../data/content";
import { Reveal } from "../shared/Reveal";
import { SectionHead } from "../shared/SectionHead";
import { PrimaryButton, GhostButton } from "../shared/Buttons";

const CHAPTERS = [
  {
    num: "01",
    title: "The Call",
    text: "For the first time, Aotearoa's martial arts community gathers under one banner — a major national festival built for athletes, coaches, clubs and families.",
  },
  {
    num: "02",
    title: "Respect the Craft",
    text: "Every discipline keeps its own rules, officials and culture. Kumite Games never dilutes an art — it gives each one a bigger stage.",
  },
  {
    num: "03",
    title: "One Arena",
    text: "Competition, respect, discipline, community and sportsmanship — many martial arts, one shared celebration of everything they stand for.",
  },
];

export const AboutSection = () => (
  <section data-testid="about-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
    <SectionHead
      number="01"
      eyebrow="About Kumite Games"
      title="Many arts. One standard of honour."
      description="Kumite Games is a new multi-discipline martial arts event for New Zealand — bringing different combat sports and martial arts organisations together under one roof, while protecting the identity, rules, technical authority and culture of every discipline."
      testId="about-heading"
    />
    <div className="grid gap-px overflow-hidden border border-white/5 bg-white/5 md:grid-cols-3">
      {CHAPTERS.map((c, i) => (
        <Reveal key={c.num} delay={i * 0.12} className="spotlight-card bg-ink-soft p-8 sm:p-10">
          <span className="font-display text-6xl text-outline-gold">{c.num}</span>
          <h3 className="mt-6 font-head text-xl font-bold uppercase tracking-wide text-bone">
            {c.title}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">{c.text}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export const DisciplinesSection = () => (
  <section data-testid="disciplines-section" className="border-t border-white/5 bg-[#0A0B0E]">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <SectionHead
          number="02"
          eyebrow="The Martial Arts"
          title="Five disciplines. One stage."
          description="The founding disciplines of Kumite Games. Each art competes under the technical authority of its own participating organisation — with more disciplines to be welcomed in future editions."
          testId="disciplines-heading"
        />
        <Reveal delay={0.2}>
          <GhostButton to="/disciplines" testId="disciplines-view-all-button">
            All Martial Arts
          </GhostButton>
        </Reveal>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {DISCIPLINES.map((d, i) => (
          <Reveal key={d.slug} delay={(i % 5) * 0.08}>
            <Link
              to={`/disciplines/${d.slug}`}
              data-testid={`discipline-card-${d.slug}`}
              className="group clip-frame relative block overflow-hidden border border-white/5 bg-ink-card"
            >
              <div className="relative h-56 overflow-hidden sm:h-64">
                <img
                  src={IMAGES[d.image]}
                  alt={d.name}
                  loading="lazy"
                  className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-card via-ink-card/20 to-transparent" />
                <span className="absolute left-4 top-4 border border-gold/30 bg-black/60 px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-gold backdrop-blur-sm">
                  {d.family}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-head text-lg font-bold uppercase tracking-wide text-bone transition-colors duration-300 group-hover:text-kred">
                    {d.name}
                  </h3>
                  <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-slate-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-kred" />
                </div>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  {d.origin} — {d.tag}
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ARENA_ICONS = [LayoutGrid, Swords, Box, Layers];

export const ArenasSection = () => (
  <section data-testid="arenas-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
    <SectionHead
      number="03"
      eyebrow="One Event — Multiple Arenas"
      title="A festival that never stops moving"
      description="Multiple competition areas operating simultaneously within one major event. Arenas will be matched to disciplines as participating organisations are confirmed."
      testId="arenas-heading"
    />
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {ARENAS.map((a, i) => {
        const Icon = ARENA_ICONS[i];
        return (
          <Reveal
            key={a.name}
            delay={i * 0.1}
            className="spotlight-card clip-notch group border border-white/5 bg-ink-soft p-8 transition-colors duration-300 hover:border-kred/40"
          >
            <Icon className="h-7 w-7 text-kred transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110" />
            <h3 className="mt-6 font-head text-lg font-bold uppercase tracking-wide text-bone">
              {a.name}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{a.text}</p>
          </Reveal>
        );
      })}
    </div>
  </section>
);

export const FederationsSection = () => (
  <section data-testid="federations-section" className="relative isolate overflow-hidden border-t border-white/5">
    <div className="absolute inset-0 -z-10">
      <img src={IMAGES.muaythai} alt="" loading="lazy" className="h-full w-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/90 to-ink/60" />
    </div>
    <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 sm:py-36 lg:grid-cols-2 lg:gap-20">
      <div>
        <SectionHead
          number="04"
          eyebrow="Federations & Organisations"
          title="Built with the guardians of each art"
          testId="federations-heading"
        />
        <Reveal>
          <p className="text-base leading-relaxed text-slate-300">
            Kumite Games intends to work alongside recognised martial arts organisations and
            governing bodies — never in place of them.
          </p>
          <p className="mt-5 text-base leading-relaxed text-slate-400">
            Each participating organisation maintains technical authority over its discipline,
            including competition rules, officiating standards and sport-specific requirements.
            Kumite Games provides the overall event platform, venue experience, promotion and
            multi-sport environment.
          </p>
          <div className="mt-10">
            <PrimaryButton to="/federations" testId="federations-cta-button">
              Become a Participating Organisation
            </PrimaryButton>
          </div>
        </Reveal>
      </div>
      <div className="flex flex-col justify-center gap-5">
        {[
          { icon: ShieldCheck, title: "Technical authority stays with you", text: "Rules, officiating and sport-specific standards remain under each organisation's control." },
          { icon: Trophy, title: "A bigger stage for your art", text: "Premium presentation, promotion and a national multi-sport audience." },
          { icon: Handshake, title: "A partnership model", text: "Kumite Games provides the platform; your organisation leads its discipline." },
        ].map((f, i) => (
          <Reveal key={f.title} delay={i * 0.12} className="clip-notch flex gap-5 border border-white/10 bg-black/50 p-6 backdrop-blur-sm">
            <f.icon className="h-6 w-6 shrink-0 text-gold" />
            <div>
              <h3 className="font-head text-base font-bold uppercase tracking-wide text-bone">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const ExperienceSections = () => (
  <section data-testid="experience-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
    <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
      <div className="order-2 lg:order-1">
        <Reveal className="clip-frame relative overflow-hidden">
          <img src={IMAGES.athlete} alt="Athlete preparing for competition" loading="lazy" className="h-[420px] w-full object-cover sm:h-[520px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
          <span className="absolute bottom-6 left-6 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
            The Athlete Experience
          </span>
        </Reveal>
      </div>
      <div className="order-1 flex flex-col justify-center lg:order-2">
        <SectionHead
          number="05"
          eyebrow="For Athletes"
          title="Step onto a professional stage"
          testId="athletes-heading"
        />
        <Reveal>
          <ul className="space-y-4">
            {[
              "A high-quality competition environment built around the athlete",
              "Professional, international-standard event presentation",
              "Multiple martial arts communities competing side by side",
              "Athlete recognition, official results and event honours",
              "Future rankings and medal tables as Kumite Games grows",
            ].map((item) => (
              <li key={item} className="flex items-start gap-4 text-sm leading-relaxed text-slate-300 sm:text-base">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-kred" />
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <GhostButton to="/athletes" testId="athletes-info-button">
              Athlete Information
            </GhostButton>
          </div>
        </Reveal>
      </div>
    </div>

    <div className="mt-24 grid gap-16 sm:mt-36 lg:grid-cols-2 lg:gap-24">
      <div className="flex flex-col justify-center">
        <SectionHead
          number="06"
          eyebrow="For Spectators"
          title="A day out for the whole community"
          testId="spectators-heading"
        />
        <Reveal>
          <p className="text-base leading-relaxed text-slate-400">
            Kumite Games is being designed for families, supporters and martial arts fans as much
            as for competitors — multiple competitions running at once, a live arena atmosphere,
            food and vendors, martial arts demonstrations, and a community celebration around it
            all.
          </p>
          <p className="mt-5 text-sm italic leading-relaxed text-slate-500">
            Full spectator details, tickets and entertainment line-up will be announced once
            confirmed.
          </p>
          <div className="mt-10 flex items-center gap-6">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-kred" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Families</span>
            </div>
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-kred" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Fans</span>
            </div>
            <div className="flex items-center gap-3">
              <Swords className="h-5 w-5 text-kred" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Clubs</span>
            </div>
          </div>
        </Reveal>
      </div>
      <Reveal className="clip-frame relative overflow-hidden">
        <img src={IMAGES.spectator} alt="Combat sports action" loading="lazy" className="h-[420px] w-full object-cover sm:h-[520px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
        <span className="absolute bottom-6 left-6 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
          The Spectator Experience
        </span>
      </Reveal>
    </div>
  </section>
);

export const PartnersSection = () => (
  <section data-testid="partners-section" className="relative isolate overflow-hidden border-t border-white/5 bg-[#0A0B0E]">
    <div className="absolute inset-0 -z-10 bg-[radial-gradient(800px_circle_at_50%_0%,rgba(212,175,55,0.06),transparent_60%)]" />
    <div className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8 sm:py-36">
      <SectionHead
        align="center"
        number="07"
        eyebrow="Partners & Sponsors"
        title="Partner With Kumite Games"
        description="Kumite Games will offer premium partnership opportunities for brands, sporting organisations, community partners and commercial sponsors who want to stand alongside New Zealand's martial arts community."
        testId="partners-heading"
      />
      <Reveal delay={0.15}>
        <div className="mx-auto grid max-w-3xl grid-cols-2 gap-px overflow-hidden border border-white/5 bg-white/5 sm:grid-cols-4">
          {["Major Partner", "Event Partner", "Supplier", "Community"].map((t) => (
            <div key={t} className="grid h-24 place-items-center bg-ink-soft px-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t}</span>
            </div>
          ))}
        </div>
        <div className="mt-12">
          <PrimaryButton to="/partners" testId="partnership-enquiries-button">
            Partnership Enquiries
          </PrimaryButton>
        </div>
      </Reveal>
    </div>
  </section>
);

export const NewsSection = () => (
  <section data-testid="news-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
    <div className="flex flex-wrap items-end justify-between gap-8">
      <SectionHead
        number="08"
        eyebrow="News & Announcements"
        title="The story begins"
        description="Official announcements — the event reveal, new martial arts, federation partnerships, venue news and athlete information — will be published here."
        testId="news-heading"
      />
      <Reveal delay={0.2}>
        <GhostButton to="/news" testId="news-view-all-button">
          All News
        </GhostButton>
      </Reveal>
    </div>
    <div className="grid gap-5 md:grid-cols-3">
      {NEWS_ITEMS.slice(0, 3).map((n, i) => (
        <Reveal key={n.slug} delay={i * 0.1}>
          <Link
            to="/news"
            data-testid={`news-card-${n.slug}`}
            className="group clip-frame block overflow-hidden border border-white/5 bg-ink-card"
          >
            <div className="relative h-52 overflow-hidden">
              <img
                src={IMAGES[n.image]}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover opacity-50 transition-all duration-700 group-hover:scale-105 group-hover:opacity-75"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-card to-transparent" />
              <span className="absolute left-4 top-4 bg-kred px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-white">
                {n.tag}
              </span>
            </div>
            <div className="p-6">
              <h3 className="font-head text-lg font-bold uppercase tracking-wide text-bone transition-colors duration-300 group-hover:text-kred">
                {n.title}
              </h3>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                Coming Soon
              </p>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

export const FinalCTA = () => (
  <section data-testid="final-cta-section" className="relative isolate overflow-hidden border-t border-white/5">
    <div className="absolute inset-0 -z-10">
      <img src={IMAGES.athlete} alt="" loading="lazy" className="h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(700px_circle_at_50%_100%,rgba(220,38,38,0.18),transparent_60%)]" />
    </div>
    <div className="mx-auto max-w-5xl px-5 py-28 text-center sm:px-8 sm:py-40">
      <Reveal>
        <p className="font-mono text-xs font-bold uppercase tracking-[0.35em] text-kred">
          New Zealand • Aotearoa
        </p>
        <h2 data-testid="final-cta-heading" className="mt-6 font-display text-6xl uppercase leading-[0.9] tracking-tight text-bone sm:text-7xl lg:text-8xl">
          Be Part of <span className="text-kred">Kumite Games</span>
        </h2>
        <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-slate-400">
          Whether you lead an organisation, step onto the mats, or stand behind the event —
          there is a place for you in New Zealand's martial arts festival.
        </p>
        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <PrimaryButton to="/federations" testId="cta-organisations-button">
            Martial Arts Organisations
          </PrimaryButton>
          <GhostButton to="/athletes" testId="cta-athletes-button">
            Athletes
          </GhostButton>
          <GhostButton to="/partners" testId="cta-partners-button">
            Partners
          </GhostButton>
          <GhostButton to="/contact" testId="cta-contact-button">
            Contact Us
          </GhostButton>
        </div>
      </Reveal>
    </div>
  </section>
);
